import React, { useState, useEffect } from "react";
import { useMoney } from "../../hooks/useMoney";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { createDistributor, addProgramDistributor } from "../../features/distributor/DistributorSlice";
import { RootState } from "../../store/store";
import { Loader2, AlertCircle, ArrowLeft, Eye, EyeOff, CheckCircle } from "lucide-react";
import { ProgramForm } from "./BuildCataloguePage";

/* --------------------------------- Interfaces ------------------------------ */

interface ReviewPageProps {
  currency: string;
  country: string;
  paymentType: string;
  name: string;
  businessName: string;
  hostname: string;
  programIds: ProgramForm;
  email: string;
  password: string;
  contactInformation: {
    email: string;
    phone: string;
    contactPerson: string;
    address: {
      street: string;
      city: string;
      state: string;
      country: string;
      zipCode: string;
    };
  };
  onBack: () => void;
  onBackListing: () => void;
  isPurchaseOrderRequired: boolean;
  allowPurchaseOrderCreation: boolean;
}

const ReviewPage: React.FC<ReviewPageProps> = ({ onBackListing, onBack, name, businessName, hostname, paymentType, currency, country, programIds, email, password, contactInformation, isPurchaseOrderRequired, allowPurchaseOrderCreation }) => {
  const { symbol } = useMoney();
  /* --------------------------------- Redux ------------------------------ */

  const dispatch = useAppDispatch();
  const { loading, error } = useAppSelector((state: RootState) => state.distributor);

  /* --------------------------------- States ------------------------------ */

  const [success, setSuccess] = useState(false);
  const [hidden, setHidden] = useState(true);

  /* --------------------------------- Hooks ------------------------------ */

  useEffect(() => {
    if (success) {
      onBackListing();
    }
  }, [success, onBackListing]);

  /* ------------------------------ Event Handlers ---------------------------- */

  const handleCreateDistributor = async () => {
    const response1 = await dispatch(
      createDistributor({
        name,
        businessName,
        hostname,
        contactInformation: {
          email: contactInformation.email,
          phone: contactInformation.phone,
          contactPerson: contactInformation.contactPerson,
          address: {
            city: contactInformation.address.city,
            state: contactInformation.address.state,
            street: contactInformation.address.street,
            country: contactInformation.address.country,
            zipCode: contactInformation.address.zipCode,
          },
        },
        paymentType: parseInt(paymentType, 10),
        currency,
        country,
        email,
        password,
        isPurchaseOrderRequired,
        allowPurchaseOrderCreation
      }),
    );
    const distributorId = response1.payload.distributorId || "";
    if (distributorId) {
      const response2 = await dispatch(addProgramDistributor({ distributorId, program: programIds }));

      if (response2.type === "distributor/addProgramDistributor/fulfilled") setSuccess(true);
    }
  };

  /* ------------------------------ UI Handlers ---------------------------- */

  return (
    <div className="bg-white h-full">
      {/* Header */}
      <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-7 py-8">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Review
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Distributor <strong>{name}</strong>
            </p>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-8 sm:px-6 lg:px-8">
        {/* Error State */}
        {error && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder mb-8">
            <div className="flex items-center gap-2 justify-center text-red-500">
              <AlertCircle className="w-5 h-5" />
              <span>{error ? error : "Something went wrong!"}</span>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder mb-8">
            <div className="flex justify-center items-center gap-2">
              <Loader2 className="w-10 h-10 animate-spin text-brand/80" />
              <span className="text-sm text-gray-500">Creating distributor...</span>
            </div>
          </div>
        )}

        {/* Review Content */}
        {!loading && !error && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Left Section - Distributor Details */}
            <div className="bg-white rounded-xl p-6 border-[1px] border-cardborder">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Distributor Details</h2>

              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-600">Name</span>
                  <span className="text-sm text-gray-900">{name}</span>
                </div>

                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-600">Business Name</span>
                  <span className="text-sm text-gray-900">{businessName}</span>
                </div>

                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-600">Payment Type</span>
                  <span className="text-sm text-gray-900">{paymentType === "0" ? "Prepaid" : "Postpaid"}</span>
                </div>

                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-600">Currency</span>
                  <span className="text-sm text-gray-900">{currency}</span>
                </div>

                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-600">Country</span>
                  <span className="text-sm text-gray-900">{country}</span>
                </div>

                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-600">Email</span>
                  <span className="text-sm text-gray-900">{email}</span>
                </div>

                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-600">Password</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-900">
                      {hidden ? "••••••••" : password}
                    </span>
                    <button
                      onClick={() => setHidden(!hidden)}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                      {hidden ? (
                        <Eye className="w-4 h-4 text-gray-500" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-gray-500" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="py-3">
                  <span className="text-sm font-medium text-gray-600 block mb-3">Contact Information</span>
                  <div className="text-sm text-gray-900 space-y-1">
                    <div>Phone: {contactInformation.phone}</div>
                    <div>Contact Person: {contactInformation.contactPerson}</div>
                    <div>Address: {contactInformation.address.street}</div>
                    <div>{contactInformation.address.city}, {contactInformation.address.state}</div>
                    <div>{contactInformation.address.country}, {contactInformation.address.zipCode}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Section - Available Programs */}
            <div className="bg-white rounded-xl p-6 border-[1px] border-cardborder">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">
                Available Programs <span className="text-brand">({Object.keys(programIds).length})</span>
              </h2>

              <div className="space-y-4">
                {Object.entries(programIds).map(([programId, value]) => (
                  <div key={programId} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={value.program_details.logo}
                        alt={value.program_details.displayName}
                        className="w-16 h-10 object-contain rounded-lg p-1"
                      />
                      <h3 className="text-sm font-semibold text-gray-900">
                        {value.program_details.displayName}
                      </h3>
                    </div>

                    <div className="space-y-2">
                      <span className="text-xs font-medium text-gray-600">Discount Ranges:</span>
                      <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                        {value.discount_ranges.map((range, index) => (
                          <div key={index} className="flex items-center gap-2 text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            <span className="flex-1">
                              {symbol}{range.min_order_value} to {range.max_order_value === null ? '∞' : `${symbol}${range.max_order_value}`}
                            </span>
                            <span className="text-brand font-semibold">
                              {range.discount}% discount
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {!loading && !error && (
          <div className="flex justify-between pb-8">
            <button
              type="button"
              onClick={onBack}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none transition-colors"
            >
              Back
            </button>

            <button
              type="button"
              onClick={handleCreateDistributor}
              className="px-6 py-2 text-sm font-medium text-white rounded-md bg-brand hover:bg-brand/80 focus:outline-none transition-colors"
            >
              Create Distributor
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewPage;

